import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageStatus, MessageType, useWebSocketContext } from "@moonscape/shared";

/**
 * How long to wait for a settling frame before showing an error with a retry.
 *
 * The server answers this message with a "processing" frame and then either a
 * "completed", "failed" or "denied" one. Two live shapes leave the second frame
 * missing: a throw the message loop swallows (guarded server-side now, but the
 * client cannot assume the deployed server is current), and the socket dying
 * mid-request — prod logs show close code 1006 shortly after connect. Either way
 * the screen must resolve to something a user can act on.
 *
 * 30s is the service's defensible worst case: one balances query plus live calls
 * to two price providers, none of which sets a request timeout of its own, so
 * this is the only bound on the round trip. It is also one full server ping
 * cycle (ws.ts pings every 30s), so a silently dead socket has been detected by
 * the time this fires.
 */
const TLH_TIMEOUT_MS = 30_000;

export function useWebSocketTaxLossHarvesting() {
  const { sendMessageAsync, isConnected } = useWebSocketContext();
  const queryClient = useQueryClient();
  const [attempt, setAttempt] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  // The query never fetches. Its cache entry is written by WebsocketContext
  // from the socket frame, so the payload type is the frame envelope, not the
  // null this queryFn returns.
  const query = useQuery<any>({
    queryKey: [MessageType.TAX_LOSS_HARVESTING],
    queryFn: async () => null,
    enabled: false,
  });

  useEffect(() => {
    if (isConnected) {
      sendMessageAsync({
        type: MessageType.TAX_LOSS_HARVESTING,
      });
    }
  }, [isConnected, sendMessageAsync, attempt]);

  const status = query?.data?.status;
  // Any status other than "processing" settles the request: "completed",
  // "failed", "denied". Absence of a status means no frame has landed yet.
  const isSettled = !!status && status !== MessageStatus.PROCESSING;

  useEffect(() => {
    if (isSettled) return;
    const timer = setTimeout(() => setTimedOut(true), TLH_TIMEOUT_MS);
    return () => clearTimeout(timer);
    // isConnected re-arms the clock across a reconnect, which re-sends above.
  }, [isSettled, isConnected, attempt]);

  const retry = useCallback(() => {
    queryClient.setQueryData([MessageType.TAX_LOSS_HARVESTING], null);
    setTimedOut(false);
    setAttempt((a) => a + 1);
  }, [queryClient]);

  // `timedOut` latches until the next retry, so it has to yield to a frame that
  // arrives late. The 30s bound exists because the price providers set no
  // timeout of their own — a response at t=31s is a slow day, not an anomaly,
  // and the payload is already in the cache by the time we would call it failed.
  const isFailed = status === MessageStatus.FAILED || (timedOut && !isSettled);

  return {
    ...query,
    data: query?.data?.responseMessage,
    isQueryLoading: !isSettled && !timedOut,
    isDenied: status === MessageStatus.DENIED,
    isFailed,
    failureMessage: isFailed
      ? query?.data?.responseMessage?.error ||
        "We could not load your harvestable losses. Check your connection and try again."
      : null,
    retry,
  };
}
